import * as THREE from "three";
import gsap from "gsap";

import { createScene as createStart } from "./scenes/startScene.js";
import { createScene as createApps } from "./scenes/appsScene.js";
import { createScene as createCyber } from "./scenes/cyberScene.js";
import { createScene as createContact } from "./scenes/contactScene.js";
import { createScene as createGallery } from "./scenes/galleryScene.js";

import fsMatMainVert from "./shaders/someGlobalVert.glsl";
import fsMatMainFrag from "./shaders/fsMatMainFrag.glsl";
import copyMainVert from "./shaders/someGlobalVert.glsl";
import copyMainFrag from "./shaders/copyMainFrag.glsl";

/* ------------------------------------------------ */
/* Configuration & State                            */
/* ------------------------------------------------ */

const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
const isCoarse =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(pointer: coarse)").matches;

const isMobile =
  /Mobi|Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(ua) || isCoarse;

const memory = navigator.deviceMemory || 4;

const isLowEnd = isMobile && (navigator.hardwareConcurrency <= 4 || memory < 4);

const isMid = isMobile && !isLowEnd;

const Config = {
  MAX_DPR: isLowEnd
    ? 1
    : isMid
      ? 1.5
      : Math.min(window.devicePixelRatio || 1, 2),

  RT_SCALE: isLowEnd ? 0.75 : isMid ? 1.0 : 1.0,
  WHEEL_LOCK_MS: 800,
  SWIPE_THRESHOLD: 75,
  WHEEL_THRESHOLD: 120,
  WHEEL_MAX_STEP: 160,
  BLOOM_FADE_DURATION: 2.0,
  TARGET_BLOOM_STRENGTH: 1.0,
  TRANSITION_DURATION: 2.5,
  MOBILE_TRAIL_DELAY: 0.4,
  MAX_SCROLL_INDEX: 3,
};

const State = {
  scenes: [],
  activeIndex: 0,
  subStep: 0,
  isTransitioning: false,
  wheelLocked: false,
  scrollBlocked: false,
  isIntroFinished: false,
  bloomFadeTime: 0,
  touchStartY: 0,
  heartMode: false,
  overlayTimeout: null,
  backBtnTimeout: null,
  wheelAcc: 0,
  wheelResetTimeout: null,
  autoIntroTimeout: null,
  autoIntroTween: null,
  autoReturnTimeout: null,
  autoReturnTween: null,
  isReturning: false,
  exitAnimating: false,
  exitAnimatingIndex: 4,
};

function clearAutoIntro() {
  if (State.autoIntroTimeout) {
    clearTimeout(State.autoIntroTimeout);
    State.autoIntroTimeout = null;
  }

  if (State.autoIntroTween) {
    State.autoIntroTween.kill();
    State.autoIntroTween = null;
  }
}

const DOM = {
  canvas: document.querySelector("#webgl"),
  sections: document.querySelectorAll("section[data-index]"),
  btnSynxius: document.querySelector(".btnSynxius"),
  btnProjects: document.querySelector(".btnApp-projects"),
  galleryBack: document.querySelector("#gallery-back"),
  langBtn: document.querySelector("#eng"),
};

/* ------------------------------------------------ */
/* Loading Manager                                  */
/* ------------------------------------------------ */

const loadingManager = new THREE.LoadingManager();

loadingManager.onProgress = (url, itemsLoaded, itemsTotal) => {
  if (window.assetLoadingStatus) {
    window.assetLoadingStatus.progress = (itemsLoaded / itemsTotal) * 100;
  }
};

loadingManager.onLoad = () => {
  if (window.assetLoadingStatus) {
    window.assetLoadingStatus.progress = 100;
    window.assetLoadingStatus.isFinished = true;
  }
};

/* ------------------------------------------------ */
/* Core Three.js Setup                              */
/* ------------------------------------------------ */

const renderer = new THREE.WebGLRenderer({
  canvas: DOM.canvas,
  antialias: true,
});
const dpr = Math.min(window.devicePixelRatio, Config.MAX_DPR);

renderer.setPixelRatio(dpr);
const initW = Math.max(1, window.innerWidth);
const initH = Math.max(1, window.innerHeight);
renderer.setSize(initW, initH);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.0;
renderer.autoClear = true;

const camera = new THREE.PerspectiveCamera(
  50,
  window.innerWidth / window.innerHeight,
  0.1,
  1000,
);
camera.position.set(0, 0, 3);

/* ------------------------------------------------ */
/* Render Targets & Fullscreen Quad                 */
/* ------------------------------------------------ */

function ensureCanvasSize() {
  const w = Math.max(1, DOM.canvas.clientWidth || window.innerWidth);
  const h = Math.max(1, DOM.canvas.clientHeight || window.innerHeight);

  renderer.setSize(w, h);
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
}

function makeRT(scale) {
  const w = Math.max(
    1,
    Math.floor((DOM.canvas.clientWidth || window.innerWidth) * scale * dpr),
  );
  const h = Math.max(
    1,
    Math.floor((DOM.canvas.clientHeight || window.innerHeight) * scale * dpr),
  );
  const rt = new THREE.WebGLRenderTarget(w, h, {
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    format: THREE.RGBAFormat,
    colorSpace: THREE.NoColorSpace,
  });
  rt.texture.wrapS = rt.texture.wrapT = THREE.ClampToEdgeWrapping;
  return rt;
}

ensureCanvasSize();

const rtFrom = makeRT(Config.RT_SCALE);
const rtTo = makeRT(Config.RT_SCALE);

const fsScene = new THREE.Scene();
const fsCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

const fsUniforms = {
  tFrom: { value: null },
  tTo: { value: null },
  uProgress: { value: 0 },
  uTime: { value: 0 },
  uScrollDir: { value: new THREE.Vector2(1, 0) },
};

const fsMat = new THREE.ShaderMaterial({
  uniforms: fsUniforms,
  vertexShader: fsMatMainVert,
  fragmentShader: fsMatMainFrag,
  extensions: {
    derivatives: true,
  },
});

fsScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), fsMat));

const copyMat = new THREE.ShaderMaterial({
  uniforms: { tDiffuse: { value: null } },
  vertexShader: copyMainVert,
  fragmentShader: copyMainFrag,
  extensions: {
    derivatives: true,
  },
  depthWrite: false,
  depthTest: false,
});
const copyScene = new THREE.Scene();
const copyCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
copyScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), copyMat));

/* ------------------------------------------------ */
/* Privacy Policy Modal Management                  */
/* ------------------------------------------------ */

document.addEventListener("DOMContentLoaded", () => {
  const policyModal = document.getElementById("global-policy-modal");
  const contentArea = document.getElementById("policy-content-area");
  const openBtn = document.getElementById("open-policy");
  const closeBtn = document.getElementById("close-policy-btn");

  if (openBtn && policyModal) {
    openBtn.addEventListener("click", async (e) => {
      e.preventDefault();
      policyModal.classList.remove("hidden");
      document.body.classList.add("lock-scroll");
      document.documentElement.style.cursor = "none";

      if (contentArea.innerHTML.trim() === "") {
        contentArea.innerHTML = "<p>Nawiązywanie połączenia...</p>";
        try {
          const response = await fetch("/privacy-policy.html");
          contentArea.innerHTML = await response.text();
        } catch (err) {
          contentArea.innerHTML =
            "<p style='color:red;'>Błąd pobierania danych.</p>";
        }
      }
    });

    const closeModal = () => {
      policyModal.classList.add("hidden");
      document.body.classList.remove("lock-scroll");
      document.documentElement.style.cursor = "";
    };

    closeBtn.addEventListener("click", closeModal);

    const stopPropagation = (e) => e.stopPropagation();

    policyModal.addEventListener("wheel", stopPropagation, { passive: false });
    policyModal.addEventListener("touchstart", stopPropagation, {
      passive: false,
    });
    policyModal.addEventListener("touchmove", stopPropagation, {
      passive: false,
    });
    policyModal.addEventListener("touchend", stopPropagation, {
      passive: false,
    });

    policyModal.addEventListener("click", (e) => {
      if (e.target === policyModal) closeModal();
    });

    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !policyModal.classList.contains("hidden"))
        closeModal();
    });
  }
});

/* ------------------------------------------------ */
/* HTML UI Management                               */
/* ------------------------------------------------ */

const chars =
  "アァカサタナハマヤャラワガザダバパイィキシチニヒミリヰギジヂビピウゥクスツヌフムユュルグズブヅプエェケセテネヘメレゲゼデベペオォコソトノホモヨョロゴゾドボポヴッン<>-_\\/[]{}—=+*^?#_";

function setupMatrixButton(button) {
  const originalText = button.innerText.trim();
  button.innerHTML = originalText
    .split("")
    .map((char) => `<span data-char="${char}">${char}</span>`)
    .join("");

  const spans = button.querySelectorAll("span");
  let intervals = [];

  function triggerShuffle() {
    button.classList.add("is-shuffling");

    spans.forEach((span, index) => {
      let iteration = 0;
      setTimeout(() => {
        const interval = setInterval(() => {
          span.innerText = chars[Math.floor(Math.random() * chars.length)];
          span.classList.add("changing");

          iteration += 1;

          if (iteration > 10) {
            clearInterval(interval);
            span.innerText = originalText[index];
            span.classList.remove("changing");

            if (index === spans.length - 1) {
              button.classList.remove("is-shuffling");
              scheduleNextShuffle();
            }
          }
        }, 50);
        intervals.push(interval);
      }, index * 50);
    });
  }

  function scheduleNextShuffle() {
    setTimeout(triggerShuffle, Math.random() * 3000 + 2000);
  }

  scheduleNextShuffle();
}

document.querySelectorAll(".btn-matrix").forEach(setupMatrixButton);

function initSceneIndicator() {
  let indicator = document.querySelector(".scene-indicator");
  if (!indicator) {
    indicator = document.createElement("div");
    indicator.className = "scene-indicator";

    for (let i = 0; i < 4; i++) {
      const btn = document.createElement("button");
      btn.className = "scene-dot inactive";
      btn.type = "button";
      btn.dataset.index = i;
      btn.setAttribute("aria-label", `Scena ${i + 1}`);
      indicator.appendChild(btn);
    }

    document.body.appendChild(indicator);
  }

  const dots = Array.from(indicator.querySelectorAll(".scene-dot"));
  window._sceneIndicator = { el: indicator, dots };

  dots.forEach((dot) => {
    if (dot.dataset.bound === "1") return;
    dot.dataset.bound = "1";

    dot.addEventListener("click", (ev) => {
      const idx = Number(ev.currentTarget.dataset.index);
      if (!Number.isFinite(idx) || State.isTransitioning) return;
      transitionTo(idx);
    });
  });

  const prev = window.updateIndicatorForSceneIndex || function () {};
  window.updateIndicatorForSceneIndex = function (index) {
    try {
      prev(index);
    } catch (e) {}
    updateSceneIndicator(index);
  };

  updateSceneIndicator(State.activeIndex, true);
}

function updateSceneIndicator(index, instant = false) {
  const S = window._sceneIndicator;
  if (!S || !Array.isArray(S.dots)) return;

  const prev = S.el.querySelector(".scene-dot.active");
  const next = S.dots[index];

  if (!next || prev === next) return;

  gsap.killTweensOf([prev, next]);
  next.classList.add("active");
  next.classList.remove("inactive");

  if (instant) {
    if (prev) {
      prev.classList.remove("active");
      prev.classList.add("inactive");
      gsap.set(prev, { opacity: 0.7, scale: 1, filter: "blur(0px)" });
    }
    gsap.set(next, { opacity: 1, scale: 1.12, filter: "blur(0px)" });
    return;
  }

  if (prev) {
    prev.classList.remove("active");
    prev.classList.add("inactive");

    gsap.to(prev, {
      opacity: 0.7,
      scale: 1,
      filter: "blur(0px)",
      duration: 0.45,
      ease: "power2.out",
    });
  }
  gsap.to(next, {
    opacity: 1,
    scale: 1.12,
    filter: "blur(0px)",
    duration: 0.55,
    ease: "power2.out",
  });
}

function setupHTMLTexts() {
  const headers = document.querySelectorAll(
    ".hero-text-style, .cyberHeader, .appHeader, .contactHeader",
  );

  headers.forEach((headerEl) => {
    if (headerEl.dataset.split === "1") return;

    const text =
      headerEl.getAttribute("data-plain") || headerEl.textContent.trim();
    headerEl.textContent = "";

    let letterClass = "heroLetter";
    if (headerEl.classList.contains("cyberHeader")) letterClass = "cyberLetter";
    else if (headerEl.classList.contains("appHeader"))
      letterClass = "appLetter";
    else if (headerEl.classList.contains("contactHeader"))
      letterClass = "contactLetter";

    const fragment = document.createDocumentFragment();
    const staggerMs = 100;
    let idx = 0;
    let smallText = false;

    for (const ch of text) {
      if (ch === "\n") {
        fragment.appendChild(document.createElement("br"));
        continue;
      }

      if (ch === "|") {
        smallText = true;
        continue;
      }

      const span = document.createElement("span");
      span.textContent = ch;

      if (smallText) {
        span.className = letterClass + " hero-subtext";
      } else {
        span.className = letterClass;
      }

      if (ch !== " ") {
        span.style.transitionDelay = `${(idx * staggerMs) / 1000}s`;
        idx++;
      } else {
        span.style.whiteSpace = "pre";
        span.classList.add("show");
      }

      fragment.appendChild(span);
    }

    headerEl.appendChild(fragment);
    headerEl.dataset.split = "1";
  });
}

function hideAllOverlays() {
  document
    .querySelectorAll(".section > div, .section > button, p[class*='Sub']")
    .forEach((el) => {
      el.classList.add("jsHidden");
      el.classList.remove("show");
    });

  document.querySelectorAll(".section span[class*='Letter']").forEach((l) => {
    if (l.textContent !== " ") l.classList.remove("show");
  });
}

function updateSubStepUI(index, step) {
  const section = Array.from(DOM.sections).find(
    (s) => s.dataset.index == index,
  );
  if (!section) return;

  const prefix = index === 1 ? "cyber" : "app";
  const sub1 = section.querySelector(`.${prefix}Sub1`);
  const sub2 = section.querySelector(`.${prefix}Sub2`);
  const btn = index === 1 ? DOM.btnSynxius : DOM.btnProjects;

  const toggle = (el, isVisible) => {
    if (!el) return;
    if (isVisible) {
      el.hidden = false;
      el.classList.remove("jsHidden");
      requestAnimationFrame(() => el.classList.add("show"));
    } else {
      el.classList.remove("show");
      setTimeout(() => {
        if (!el.classList.contains("show")) el.classList.add("jsHidden");
      }, 500);
    }
  };

  toggle(sub1, step === 1);
  toggle(sub2, step === 2);
  toggle(btn, step === 3);
}

function hideSubtitlesOnly() {
  document
    .querySelectorAll("p[class*='Sub'], .btnSynxius, .btnApp-projects")
    .forEach((el) => {
      el.classList.remove("show");

      setTimeout(() => {
        if (!el.classList.contains("show")) {
          el.classList.add("jsHidden");
        }
      }, 100);
    });
}

function showOverlay(index) {
  if (State.overlayTimeout) {
    clearTimeout(State.overlayTimeout);
    State.overlayTimeout = null;
  }

  hideAllOverlays();

  State.overlayTimeout = setTimeout(() => {
    const section = Array.from(DOM.sections).find(
      (sec) => sec.dataset.index == index,
    );
    if (!section) return;

    section.querySelectorAll("span[class*='Letter']").forEach((letter) => {
      if (letter.textContent.trim() !== "") {
        setTimeout(() => letter.classList.add("show"), Math.random() * 200);
      }
    });

    if (index === 1 || index === 2) {
      const prefix = index === 1 ? "cyber" : "app";

      const headerContainer = section.querySelector(`.${prefix}Header`);
      if (headerContainer) {
        headerContainer.hidden = false;
        headerContainer.classList.remove("jsHidden");
        headerContainer.classList.add("show");
      }

      const subContainer = section.querySelector(`.${prefix}Sub`);
      if (subContainer) {
        subContainer.hidden = false;
        subContainer.classList.remove("jsHidden");
        subContainer.classList.add("show");
      }

      updateSubStepUI(index, State.subStep);
    } else {
      section
        .querySelectorAll(":scope > div, :scope > button")
        .forEach((el) => {
          el.hidden = false;
          el.classList.remove("jsHidden");
          el.classList.add("show");
        });
      updateGlobalButtons(index);
    }
  }, 50);
}

function updateGlobalButtons(index) {
  const btnPrivacy = document.querySelector(".btnPrivacy");
  const buttons = [
    DOM.btnSynxius,
    DOM.btnProjects,
    DOM.galleryBack,
    DOM.langBtn,
    btnPrivacy,
  ];

  buttons.forEach((btn) => {
    if (!btn) return;
    btn.classList.remove("show");
    btn.hidden = true;
    btn.setAttribute("aria-hidden", "true");
  });

  const showBtn = (btn) => {
    if (!btn) return;
    btn.hidden = false;
    btn.setAttribute("aria-hidden", "false");
    setTimeout(() => btn.classList.add("show"), 50);
  };
  if (index === 3) {
    showBtn(btnPrivacy);
  }
  if (index === 4) showBtn(DOM.galleryBack);
}

function updateGalleryBackVisibility() {
  const show = !!window.__carouselActive;
  clearTimeout(State.backBtnTimeout);

  const toggleBtns = (isHidden) => {
    [DOM.galleryBack, DOM.langBtn].forEach((btn) => {
      if (!btn) return;
      btn.setAttribute("aria-hidden", isHidden ? "true" : "false");
      if (isHidden) {
        btn.classList.remove("show");
      } else {
        btn.hidden = false;
        btn.classList.remove("show");
      }
    });
  };

  if (show) {
    toggleBtns(false);
    State.backBtnTimeout = setTimeout(() => {
      requestAnimationFrame(() => {
        if (DOM.galleryBack) DOM.galleryBack.classList.add("show");
        if (DOM.langBtn) DOM.langBtn.classList.add("show");
      });
    }, 1000);
  } else {
    toggleBtns(true);
    setTimeout(() => {
      if (!window.__carouselActive) {
        if (DOM.galleryBack) DOM.galleryBack.hidden = true;
        if (DOM.langBtn) DOM.langBtn.hidden = true;
      }
    }, 1000);
  }
}

window.setCarouselActive = function (active) {
  window.__carouselActive = !!active;
  updateGalleryBackVisibility();
};

/* ------------------------------------------------ */
/* Scene Management Helpers                         */
/* ------------------------------------------------ */

async function ensureResolvedScene(maybeScene) {
  return typeof maybeScene?.then === "function" ? await maybeScene : maybeScene;
}

function getThreeScene(sceneObj) {
  if (!sceneObj) return null;
  for (const key in sceneObj) {
    if (sceneObj[key] instanceof THREE.Scene) return sceneObj[key];
  }
  return null;
}

async function snapshotSceneToRT(sceneObjOrPromise, target, dt = 1 / 60) {
  const sceneObj = await ensureResolvedScene(sceneObjOrPromise);
  if (!sceneObj || !target || target.width === 0) return;

  sceneObj.update?.(dt);

  const oldRT = renderer.getRenderTarget();
  const oldClearColor = new THREE.Color();
  renderer.getClearColor(oldClearColor);
  const oldClearAlpha = renderer.getClearAlpha();

  if (sceneObj.composer) {
    const passStates = sceneObj.composer.passes.map((p) => p.renderToScreen);

    sceneObj.composer.passes.forEach((p) => {
      if (p.renderToScreen !== undefined) p.renderToScreen = false;
    });

    sceneObj.composer.render();

    sceneObj.composer.passes.forEach((p, i) => {
      if (p.renderToScreen !== undefined) p.renderToScreen = passStates[i];
    });

    copyMat.uniforms.tDiffuse.value = sceneObj.composer.readBuffer.texture;
    renderer.setRenderTarget(target);
    renderer.clear(true, true, true);
    renderer.render(copyScene, copyCamera);
  } else {
    renderer.setRenderTarget(target);
    renderer.clear(true, true, true);
    const ts = getThreeScene(sceneObj);
    if (ts) renderer.render(ts, camera);
  }

  renderer.setRenderTarget(oldRT);
  renderer.setClearColor(oldClearColor, oldClearAlpha);
}

/* ------------------------------------------------ */
/* Transitions & Navigation                         */
/* ------------------------------------------------ */

function handleWheelIntent(rawDeltaY) {
  if (
    !State.isIntroFinished ||
    State.isTransitioning ||
    State.wheelLocked ||
    State.scrollBlocked
  ) {
    return;
  }

  const clamped = THREE.MathUtils.clamp(
    rawDeltaY,
    -Config.WHEEL_MAX_STEP,
    Config.WHEEL_MAX_STEP,
  );

  State.wheelAcc += clamped;

  clearTimeout(State.wheelResetTimeout);
  State.wheelResetTimeout = setTimeout(() => {
    State.wheelAcc = 0;
  }, 120);

  if (Math.abs(State.wheelAcc) >= Config.WHEEL_THRESHOLD) {
    const dir = Math.sign(State.wheelAcc);
    State.wheelAcc = 0;
    fsUniforms.uScrollDir.value.set(dir > 0 ? 1.0 : -1.0, 0.0);
    navigateByDelta(dir);
  }
}

function toggleScrollBlock(block) {
  State.scrollBlocked = block;
  const overflow = block ? "hidden" : "";
  document.documentElement.style.overflow = overflow;
  document.body.style.overflow = overflow;
}
function clearAutoMotion() {
  if (State.autoIntroTimeout) {
    clearTimeout(State.autoIntroTimeout);
    State.autoIntroTimeout = null;
  }
  if (State.autoReturnTimeout) {
    clearTimeout(State.autoReturnTimeout);
    State.autoReturnTimeout = null;
  }

  if (State.autoIntroTween) {
    State.autoIntroTween.kill();
    State.autoIntroTween = null;
  }
  if (State.autoReturnTween) {
    State.autoReturnTween.kill();
    State.autoReturnTween = null;
  }
}

function resetCamera() {
  camera.position.set(0, 0, 3);
  camera.rotation.set(0, 0, 0);
  camera.quaternion.set(0, 0, 0, 1);
  camera.up.set(0, 1, 0);
  camera.lookAt(0, 0, 0);
  camera.updateProjectionMatrix();
}

// linkowanie

const ROUTES = {
  "/": 0,
  "/start": 0,
  "/cyber": 1,
  "/apps": 2,
  "/contact": 3,
  "/gallery": 4,
};

const SEO_DATA = {
  0: {
    title: "Start | Moje Portfolio",
    desc: "Witaj w interaktywnym świecie Three.js.",
  },
  1: {
    title: "CyberSec | Law",
    desc: "Poznaj projekty łączące kod i LegalTech.",
  },
  2: {
    title: "Aplikacje | Web & Mobile",
    desc: "Przegląd moich aplikacji i technologii.",
  },
  3: {
    title: "Kontakt | Współpraca",
    desc: "Skontaktuj się ze mną bezpośrednio.",
  },
  4: {
    title: "Projekty | Wizualizacje",
    desc: "Interaktywna galeria projektów 3D.",
  },
};

function updateURL(index, push = true) {
  const pathMap = ["/", "/cyber", "/apps", "/contact", "/gallery"];
  const newPath = pathMap[index] || "/";

  const data = SEO_DATA[index] || SEO_DATA[0];
  document.title = data.title;

  const metaDesc = document.querySelector('meta[name="description"]');
  if (metaDesc) metaDesc.setAttribute("content", data.desc);

  const state = { index };

  if (push) {
    history.pushState(state, "", newPath);
  } else {
    history.replaceState(state, "", newPath);
  }
}

function waitForIntro() {
  return new Promise((resolve) => {
    if (State.isIntroFinished) return resolve();
    const id = setInterval(() => {
      if (State.isIntroFinished) {
        clearInterval(id);
        resolve();
      }
    }, 80);
  });
}

function getSceneFromPath() {
  const params = new URLSearchParams(window.location.search);
  const intended = params.get("to");

  const currentPath = window.location.pathname;

  if (intended) {
    history.replaceState(null, "", intended);
    return ROUTES[intended] ?? 0;
  }

  return ROUTES[currentPath] ?? 0;
}

async function applyDeepLink() {
  const target = getSceneFromPath();

  if (target <= 0 || target >= State.scenes.length) return;

  await waitForIntro();

  fsUniforms.uScrollDir.value.set(1.0, 0.0);
  transitionTo(target);
}

async function transitionTo(targetIndex, { pushHistory = true } = {}) {
  if (State.isTransitioning || targetIndex === State.activeIndex) return;

  const sceneIndicator = document.querySelector(".scene-indicator");
  if (sceneIndicator) {
    if (targetIndex === 4) {
      gsap.to(sceneIndicator, {
        opacity: 0,
        pointerEvents: "none",
        duration: 0.45,
        ease: "power2.out",
      });
    } else {
      gsap.to(sceneIndicator, {
        opacity: 1,
        pointerEvents: "auto",
        duration: 0.45,
        ease: "power2.in",
      });
    }
  }

  toggleScrollBlock(true);
  clearAutoMotion();
  clearAutoIntro();

  const from = State.scenes[State.activeIndex];
  const to = State.scenes[targetIndex];

  if (State.isReturning && typeof from.setSubStep === "function")
    from.setSubStep(1);
  if (!from.__snapshotReady) {
    if (from.renderForSnapshot)
      from.renderForSnapshot(renderer, rtFrom, camera);
    else await snapshotSceneToRT(from, rtFrom);
  }
  if (from) from.__snapshotReady = false;

  resetCamera();
  if (!State.isReturning) State.subStep = 0;
  if (to?.ready) await to.ready;
  if (to.setSubStep) to.setSubStep(State.subStep);
  if (!State.isReturning && typeof to.setSubStep === "function")
    to.setSubStep(1);
  if (to.renderForSnapshot) to.renderForSnapshot(renderer, rtTo, camera);
  else await snapshotSceneToRT(to, rtTo);

  if (to && typeof to.reset === "function") to.reset();

  hideAllOverlays();
  if (from?.deactivate && !from.__alreadyDeactivated) from.deactivate();
  if (from) from.__alreadyDeactivated = false;

  fsUniforms.tFrom.value = rtFrom.texture;
  fsUniforms.tTo.value = rtTo.texture;
  fsUniforms.uProgress.value = 0;
  fsUniforms.uTime.value = 0;

  State.isTransitioning = true;

  gsap.to(fsUniforms.uProgress, {
    value: 1,
    duration: Config.TRANSITION_DURATION,
    ease: "power2.inOut",
    onComplete: () => {
      State.activeIndex = targetIndex;
      updateURL(targetIndex, pushHistory);
      to?.activate?.();
      if (to._cachedBloomPass || to.composer?.passes) {
        const bloomPass =
          to._cachedBloomPass ??
          to.composer.passes.find((p) => "strength" in p);
        if (bloomPass) {
          bloomPass.strength = Config.TARGET_BLOOM_STRENGTH;
          State.bloomFadeTime = Config.BLOOM_FADE_DURATION;
        }
      }
      State.isTransitioning = false;
      toggleScrollBlock(false);
      State.isReturning = false;

      if (typeof updateIndicatorForSceneIndex === "function") {
        updateIndicatorForSceneIndex(State.activeIndex);
      }
      const shouldAutoAdvance =
        !State.isReturning &&
        typeof to?.zoomIn0 === "function" &&
        State.subStep === 0;

      if (shouldAutoAdvance) {
        if (to.setSubStep) to.setSubStep(1);
        State.subStep = 1;
        updateSubStepUI(targetIndex, 1);
        setTimeout(() => {
          State.wheelLocked = false;
          showOverlay(State.activeIndex);
        }, 200);
        return;
      }

      setTimeout(() => {
        State.wheelLocked = false;
      }, 200);
      if (targetIndex === 4) window.setCarouselActive(true);
      showOverlay(State.activeIndex);
    },
  });
}

function navigateByDelta(delta) {
  if (
    !State.isIntroFinished ||
    State.isTransitioning ||
    State.wheelLocked ||
    State.scrollBlocked
  )
    return;

  if (State.overlayTimeout) {
    clearTimeout(State.overlayTimeout);
    State.overlayTimeout = null;
  }

  hideSubtitlesOnly();

  const dir = delta > 0 ? 1 : -1;
  const currentScene = State.scenes[State.activeIndex];

  const hasSubStep = currentScene && typeof currentScene.zoomIn0 === "function";

  const MAX_INDEX = Number.isFinite(Config.MAX_SCROLL_INDEX)
    ? Config.MAX_SCROLL_INDEX
    : Infinity;

  const run = (tweenOrFn, nextStep, onDone) => {
    const tween = typeof tweenOrFn === "function" ? tweenOrFn() : tweenOrFn;

    if (!tween) {
      State.subStep = nextStep;
      updateSubStepUI(State.activeIndex, State.subStep);
      onDone?.();
      return;
    }

    State.wheelLocked = true;
    tween.eventCallback("onComplete", () => {
      State.subStep = nextStep;
      updateSubStepUI(State.activeIndex, State.subStep);
      State.wheelLocked = false;
      onDone?.();
    });
  };

  if (dir === 1) {
    if (hasSubStep) {
      if (State.subStep === 0) return run(currentScene.zoomIn0, 1);
      if (State.subStep === 1) return run(currentScene.zoomIn1, 2);
      if (State.subStep === 2) return run(currentScene.zoomIn2, 3);
    }

    const nextIndex = State.activeIndex + 1;

    if (nextIndex > MAX_INDEX) return;

    if (nextIndex < State.scenes.length) {
      State.isReturning = false;
      State.subStep = 0;
      transitionTo(nextIndex);
    }
  } else {
    if (hasSubStep) {
      if (State.subStep === 3) return run(currentScene.zoomOut2, 2);
      if (State.subStep === 2) return run(currentScene.zoomOut1, 1);

      if (State.subStep === 1) {
        const prevIndex = State.activeIndex - 1;

        if (prevIndex >= 0) {
          State.isReturning = true;
          State.subStep = 3;
          transitionTo(prevIndex);
        }
        return;
      }
    }

    const prevIndex = State.activeIndex - 1;
    if (prevIndex >= 0) {
      State.isReturning = true;
      State.subStep = 3;
      transitionTo(prevIndex);
    }
  }
}

window.addEventListener("goToScene", (ev) => {
  const idx = Number(ev?.detail);
  if (Number.isFinite(idx)) transitionTo(idx);
});

window.addEventListener(
  "wheel",
  (e) => {
    if (window.__galleryActive || State.scrollBlocked) return;
    handleWheelIntent(e.deltaY);
  },
  { passive: false },
);

document.addEventListener("pointerup", (e) => {
  if (e.target.closest(".btnSynxius"))
    return window.open("https://synxius.com/", "_blank");
  if (
    e.target.closest(".btnApp-projects") &&
    !State.isTransitioning &&
    State.activeIndex !== 4
  )
    return transitionTo(4);
  if (
    e.target.closest("#gallery-back") &&
    !State.isTransitioning &&
    State.activeIndex !== 2
  ) {
    const galleryScene = State.scenes[4];
    if (galleryScene && typeof galleryScene.deactivate === "function") {
      toggleScrollBlock(true);
      if (galleryScene.renderForSnapshot)
        galleryScene.renderForSnapshot(renderer, rtFrom, camera);
      else snapshotSceneToRT(galleryScene, rtFrom);

      State.exitAnimating = true;
      State.exitAnimatingIndex = 4;

      galleryScene.deactivate().then(() => {
        State.exitAnimating = false;
        toggleScrollBlock(false);
        galleryScene.__alreadyDeactivated = true;
        galleryScene.__snapshotReady = true;
        transitionTo(2);
      });
    } else {
      transitionTo(2);
    }
    return;
  }
});

window.addEventListener(
  "touchstart",
  (e) => {
    if (!window.__galleryActive) State.touchStartY = e.touches[0].clientY;
  },
  { passive: true },
);

window.addEventListener(
  "touchend",
  (e) => {
    if (
      window.__galleryActive ||
      State.isTransitioning ||
      State.wheelLocked ||
      State.scrollBlocked
    )
      return;

    const deltaY = State.touchStartY - e.changedTouches[0].clientY;
    const adjustedDelta = deltaY * 0.85;

    if (Math.abs(adjustedDelta) > Config.SWIPE_THRESHOLD) {
      fsUniforms.uScrollDir.value.set(0.0, adjustedDelta > 0 ? -1.0 : 1.0);
      navigateByDelta(adjustedDelta);
    }
  },
  { passive: true },
);

window.addEventListener("keydown", (e) => {
  if (window.__galleryActive || State.isTransitioning || State.wheelLocked)
    return;

  let delta = 0;
  if (e.code === "ArrowDown") delta = 1;
  else if (e.code === "ArrowUp") delta = -1;

  if (delta !== 0) {
    e.preventDefault();
    fsUniforms.uScrollDir.value.set(delta, 0.0);
    navigateByDelta(delta);
  }
});

window.addEventListener("startEyeWink", () => {
  const startScene = State.scenes[0];
  if (startScene?.triggerWink) startScene.triggerWink();

  if (State.activeIndex === 0) {
    setTimeout(() => {
      State.isIntroFinished = true;
    }, 1800);
  }
});
window.addEventListener("popstate", async () => {
  const target = getSceneFromPath();

  if (target === State.activeIndex) return;

  await transitionTo(target, { pushHistory: false });
});

/* ------------------------------------------------ */
/* Helper: Bloom Fading                             */
/* ------------------------------------------------ */

function handleBloomFade(active, dt) {
  if (!active.composer) return;
  if (!active._cachedBloomPass && !active._bloomPassChecked) {
    active._cachedBloomPass = active.composer.passes.find(
      (p) => "strength" in p,
    );
    active._bloomPassChecked = true;
  }

  if (active._cachedBloomPass) {
    State.bloomFadeTime += dt;
    const progress = Math.min(
      1,
      State.bloomFadeTime / Config.BLOOM_FADE_DURATION,
    );
    const easedProgress = progress * (2 - progress);
    active._cachedBloomPass.strength =
      easedProgress * Config.TARGET_BLOOM_STRENGTH;
  }
}

/* ------------------------------------------------ */
/* Main Loop                                        */
/* ------------------------------------------------ */

const updateSizes = () => {
  const w = Math.max(1, window.innerWidth);
  const h = Math.max(1, window.innerHeight);
  const currentDpr = Math.min(window.devicePixelRatio, Config.MAX_DPR);

  renderer.setPixelRatio(currentDpr);

  camera.aspect = w / h;
  camera.updateProjectionMatrix();
  renderer.setSize(w, h);

  State.scenes.forEach((s) => {
    s.onResize?.(w, h, currentDpr);
  });

  const rtW = Math.max(1, Math.floor(w * Config.RT_SCALE * currentDpr));
  const rtH = Math.max(1, Math.floor(h * Config.RT_SCALE * currentDpr));

  if (typeof rtFrom !== "undefined" && rtFrom) rtFrom.setSize(rtW, rtH);
  if (typeof rtTo !== "undefined" && rtTo) rtTo.setSize(rtW, rtH);
};

window.addEventListener("resize", updateSizes);

updateSizes();

const clock = new THREE.Clock();

function animate() {
  const dt = clock.getDelta();

  if (rtFrom.width === 0 || rtTo.width === 0) {
    requestAnimationFrame(animate);
    return;
  }

  if (State.isTransitioning) {
    fsUniforms.uTime.value += dt;

    renderer.setRenderTarget(null);
    renderer.clear();
    renderer.render(fsScene, fsCamera);

    State.bloomFadeTime = 0;
  } else {
    const activeIndex = State.exitAnimating
      ? State.exitAnimatingIndex
      : State.activeIndex;
    const active = State.scenes[activeIndex];
    if (!active) {
      requestAnimationFrame(animate);
      return;
    }

    active.update?.(dt);
    handleBloomFade(active, dt);

    if (active.composer) {
      renderer.setRenderTarget(null);
      renderer.clear();
      active.composer.render();
    } else {
      const ts = getThreeScene(active);
      if (ts) {
        renderer.setRenderTarget(null);
        renderer.clear();
        renderer.render(ts, camera);
      }
    }
  }

  requestAnimationFrame(animate);
}

/* ------------------------------------------------ */
/* Init                                             */
/* ------------------------------------------------ */

async function warmUpTransition() {
  fsUniforms.tFrom.value = rtFrom.texture;
  fsUniforms.tTo.value = rtFrom.texture;
  fsUniforms.uProgress.value = 0.001;
  fsUniforms.uTime.value = 0;

  const warmRT = new THREE.WebGLRenderTarget(2, 2, {
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    format: THREE.RGBAFormat,
  });

  const oldRT = renderer.getRenderTarget();
  renderer.setRenderTarget(warmRT);
  renderer.render(fsScene, fsCamera);
  renderer.setRenderTarget(oldRT);
  warmRT.dispose();

  fsUniforms.uProgress.value = 0;
}

function initCursor() {
  const cursor = document.querySelector(".customX");
  if (!cursor || isMobile) return;

  const size = {
    w: cursor.offsetWidth || 12,
    h: cursor.offsetHeight || 12,
  };

  const state = {
    targetX: window.innerWidth / 2 - size.w / 2,
    targetY: window.innerHeight / 2 - size.h / 2,
    x: window.innerWidth / 2 - size.w / 2,
    y: window.innerHeight / 2 - size.h / 2,
    prevX: window.innerWidth / 2 - size.w / 2,
    prevY: window.innerHeight / 2 - size.h / 2,
    rotation: 0,
    hoverScale: 1,
    currentScale: 1,
    mailHover: false,
    frontScale: 0.95,
    backScale: 1.25,
    frontX: 0,
    frontY: 0,
    backX: 0,
    backY: 0,
  };

  const setX = gsap.quickSetter(cursor, "x", "px");
  const setY = gsap.quickSetter(cursor, "y", "px");
  const setRotation = gsap.quickSetter(cursor, "rotation", "deg");
  const setScaleX = gsap.quickSetter(cursor, "scaleX");
  const setScaleY = gsap.quickSetter(cursor, "scaleY");
  const setVar = (name, value) => cursor.style.setProperty(name, value);

  window.addEventListener("pointermove", (e) => {
    state.targetX = e.clientX - size.w / 2;
    state.targetY = e.clientY - size.h / 2;
  });

  gsap.ticker.add(() => {
    const ease = 0.15;
    state.x += (state.targetX - state.x) * ease;
    state.y += (state.targetY - state.y) * ease;

    const dx = state.x - state.prevX;
    const dy = state.y - state.prevY;
    const speed = Math.hypot(dx, dy);
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);

    setX(state.x);
    setY(state.y);

    state.currentScale += (state.hoverScale - state.currentScale) * 0.3;

    if (state.mailHover) {
      setRotation(0);
      setScaleX(state.currentScale);
      setScaleY(state.currentScale);

      setVar("--front-scale", "1");
      setVar("--back-scale", "1");
      setVar("--front-x", "0px");
      setVar("--front-y", "0px");
      setVar("--back-x", "0px");
      setVar("--back-y", "0px");
    } else {
      const stretch = gsap.utils.clamp(0, 0.55, speed * 0.08);
      const squash = stretch * 0.55;

      const finalScaleX = (1 + stretch) * state.currentScale;
      const finalScaleY = (1 - squash) * state.currentScale;

      const motion = gsap.utils.clamp(0, 1, speed / 22);
      const frontNarrow = 0.95 - motion * 0.25;
      const backFull = 1.35 + motion * 0.65;
      const localOffset = 2.5 + motion * 4.5;

      state.frontScale += (frontNarrow - state.frontScale) * 0.15;
      state.backScale += (backFull - state.backScale) * 0.15;
      state.frontX += (localOffset * 0.6 - state.frontX) * 0.15;
      state.backX += (-localOffset - state.backX) * 0.15;
      state.frontY += (0 - state.frontY) * 0.15;
      state.backY += (0 - state.backY) * 0.15;

      setRotation(angle);
      setScaleX(finalScaleX);
      setScaleY(finalScaleY);

      setVar("--front-scale", String(state.frontScale));
      setVar("--back-scale", String(state.backScale));
      setVar("--front-x", `${state.frontX}px`);
      setVar("--front-y", `${state.frontY}px`);
      setVar("--back-x", `${state.backX}px`);
      setVar("--back-y", `${state.backY}px`);
    }

    state.prevX = state.x;
    state.prevY = state.y;
  });
  let heartDelay = null;

  const normalHoverElements = document.querySelectorAll(
    "button:not(.mail-link), a:not(.mail-link), .btn-matrix:not(.mail-link)",
  );

  normalHoverElements.forEach((el) => {
    el.addEventListener("mouseenter", () => {
      if (heartDelay) {
        heartDelay.kill();
        heartDelay = null;
      }
      gsap.to(state, {
        hoverScale: 0,
        mailHover: false,
        duration: 0.5,
        ease: "power3.out",
      });
    });
    el.addEventListener("mouseleave", () => {
      gsap.to(state, { hoverScale: 1, duration: 0.6, ease: "power3.out" });
    });
  });

  const mailLinks = document.querySelectorAll(".mail-link");

  mailLinks.forEach((el) => {
    el.addEventListener("pointerenter", () => {
      gsap.to(state, {
        mailHover: true,
        hoverScale: 3,
        duration: 0.2,
        ease: "power3.out",
      });
      if (heartDelay) heartDelay.kill();
      heartDelay = gsap.delayedCall(0.15, () => cursor.classList.add("heart"));
    });

    el.addEventListener("pointerleave", () => {
      if (heartDelay) {
        heartDelay.kill();
        heartDelay = null;
      }
      cursor.classList.remove("heart");
      gsap.to(state, {
        mailHover: false,
        hoverScale: 1,
        duration: 0.2,
        ease: "power3.out",
      });
    });
  });

  window.addEventListener("resize", () => {
    size.w = cursor.offsetWidth || 12;
    size.h = cursor.offsetHeight || 12;
  });
}

function initWordsAnimation() {
  const words = [
    "WITAJ",
    "你好",
    "HELLO",
    "გამარჯობა",
    "ΧΑΙΡΕ",
    "ᚼᛅᛁᛚᛅᛘ",
    "ПРИВЕТ",
    "سلام",
    "BONJOUR",
    "ሰላም",
    "WILLKOMMEN",
    "مَرْحَبًا",
    "CIAO",
    "𓇅𓇋𓇋",
    "HOLA",
    "שלום",
    "𒁲",
    "안녕하세요",
    "SALVE",
    "こんにちは",
  ];

  const el = document.getElementById("word-inner");
  if (!el) return;

  let index = 0;
  let paused = false;

  const renderWord = (wordText) => {
    el.innerHTML = "";
    el.classList.remove("fade-out");

    [...wordText].forEach((char, i) => {
      const span = document.createElement("span");
      span.textContent = char === " " ? "\u00A0" : char;
      span.className = "letter";
      span.style.cssText = `
        animation-delay: ${i * 0.07}s;
        filter: blur(20px);
        opacity: 0;
        transform: scale(1.1);
      `;
      el.appendChild(span);
    });
  };

  const triggerTransition = () => {
    if (paused) return;

    const letters = el.querySelectorAll(".letter");
    el.classList.add("fade-out");

    letters.forEach((span, i) => (span.style.animationDelay = `${i * 0.05}s`));

    const lastLetter = letters[letters.length - 1];
    if (lastLetter) {
      lastLetter.onanimationend = () => {
        if (!paused) {
          index = (index + 1) % words.length;
          renderWord(words[index]);
        }
      };
    } else {
      renderWord(words[index]);
    }
  };

  renderWord(words[index]);
  const intervalId = setInterval(triggerTransition, 3000);
  return () => clearInterval(intervalId);
}

function startLoader() {
  const UI = {
    loader: document.getElementById("loader"),
    loaderLogo: document.querySelector(".loader-logo"),
    paths: document.querySelectorAll(".letter-group"),
    counter: document.querySelector(".counter"),
    progressWrapper: document.querySelector(".loader-progress"),
    mainUiElements: [
      "#word-inner",
      "#eng",
      ".stickyScroll",
      ".scene-indicator",
    ],
    siteLogo: ".logo",
  };
  if (
    localStorage.getItem("cookies_accepted") === null &&
    document.getElementById("cookie-banner")
  ) {
    UI.mainUiElements.push("#cookie-banner");
  }

  if (!UI.loader || !UI.counter) return;

  const state = {
    progress: { value: 0 },
    finishTriggered: false,
    checkInterval: null,
  };

  const init = () => {
    gsap.set(UI.mainUiElements, { opacity: 0, visibility: "hidden", y: 20 });
    gsap.set(UI.loader, { yPercent: 0, visibility: "visible" });
    gsap.set(UI.progressWrapper, { y: 0, opacity: 1, scale: 1 });
    if (UI.loaderLogo)
      gsap.set(UI.loaderLogo, { opacity: 0, visibility: "hidden" });
    if (UI.paths.length) {
      gsap.set(UI.paths, {
        y: 40,
        opacity: 0,
        scale: 0.85,
        filter: "blur(20px)",
      });
    }

    gsap.fromTo(
      UI.progressWrapper,
      { y: 20, opacity: 0, scale: 0.9 },
      { y: 0, opacity: 1, scale: 1, duration: 0.7, ease: "expo.out" },
    );
  };

  const updateCounterDisplay = (val) => {
    const floorVal = Math.floor(val);
    UI.counter.textContent = floorVal < 10 ? `0${floorVal}` : String(floorVal);
  };

  const playFinishSequence = () => {
    const tl = gsap.timeline({
      onComplete: () => {
        window.assetLoadingStatus.isFinished = true;
        window.assetLoadingStatus.progress = 100;
        setTimeout(() => {
          if (UI.loader) UI.loader.style.display = "none";
        }, 300);
      },
    });

    tl.set(UI.loaderLogo, { opacity: 1, visibility: "visible" })
      .to(
        UI.paths,
        {
          duration: 1.2,
          y: 0,
          opacity: 1,
          scale: 1,
          filter: "blur(0px)",
          ease: "expo.out",
          stagger: 0.12,
        },
        0,
      )
      .to({}, { duration: 0.35 })
      .to(
        UI.loader,
        {
          duration: 1.0,
          yPercent: -300,
          ease: "expo.inOut",
        },
        "+=0",
      );

    tl.to(UI.siteLogo, {
      duration: 0.9,
      opacity: 1,
      visibility: "visible",
      y: 0,
      filter: "blur(0px)",
      startAt: { y: 30 },
      ease: "expo.out",
    });

    tl.to(
      "#word-inner",
      {
        duration: 0.6,
        opacity: 1,
        visibility: "visible",
        y: 0,
        ease: "power3.out",
      },
      "-=0.5",
    );

    tl.to(
      "#eng",
      {
        duration: 0.5,
        opacity: 1,
        visibility: "visible",
        y: 0,
        ease: "power3.out",
      },
      "-=0.4",
    );

    tl.call(
      () => {
        if (typeof showOverlay === "function") showOverlay(0);
      },
      null,
      "-=0.3",
    );

    tl.to(
      UI.mainUiElements,
      {
        duration: 0.9,
        opacity: 1,
        visibility: "visible",
        y: 0,
        stagger: 0.08,
        ease: "power3.out",
      },
      "<",
    );

    tl.call(
      () => {
        window.dispatchEvent(new CustomEvent("startEyeWink"));
      },
      null,
      "+=0.15",
    );
  };

  const trackProgress = () => {
    state.checkInterval = setInterval(() => {
      const status = window.assetLoadingStatus || {
        progress: 0,
        isFinished: false,
      };
      const realTarget = Math.min(100, Math.max(0, status.progress || 0));

      gsap.to(state.progress, {
        value: realTarget,
        duration: 0.18,
        ease: "power1.out",
        onUpdate: () => updateCounterDisplay(state.progress.value),
      });

      if (!state.finishTriggered && status.isFinished && realTarget >= 100) {
        state.finishTriggered = true;
        clearInterval(state.checkInterval);

        gsap.to(state.progress, {
          value: 100,
          duration: 0.2,
          onUpdate: () => updateCounterDisplay(state.progress.value),
          onComplete: playFinishSequence,
        });
      }
    }, 50);
  };

  init();
  trackProgress();
}

function initStickyIndicator() {
  const indicator = document.querySelector(".stickyScroll");
  const allSticks = Array.from(
    document.querySelectorAll('[class*="fillStick"]'),
  );
  const topSticks = allSticks.slice(0, 8);
  const bottomSticks = allSticks.slice(8, 16);

  const fadeStickGroup = (sticks, visible) => {
    if (!sticks.length) return;
    if (visible) {
      gsap.set(sticks, { display: "block", opacity: 0 });
      gsap.to(sticks, {
        opacity: 1,
        duration: 0.9,
        stagger: 0.05,
        ease: "power3.out",
      });
    } else {
      gsap.to(sticks, {
        opacity: 0,
        duration: 0.9,
        stagger: { each: 0.04, from: "center" },
        ease: "power2.inOut",
        onComplete: () => gsap.set(sticks, { display: "none" }),
      });
    }
  };

  function setIndicatorState({ top = false, bottom = false }) {
    if (!indicator) return;
    gsap.to(indicator, {
      opacity: top || bottom ? 1 : 0,
      pointerEvents: top || bottom ? "auto" : "none",
      duration: 0.35,
    });

    fadeStickGroup(topSticks, top);
    fadeStickGroup(bottomSticks, bottom);
  }

  const sceneStates = {
    0: { top: true, bottom: false },
    1: { top: true, bottom: true },
    2: { top: true, bottom: true },
    3: { top: false, bottom: true },
    4: { top: true, bottom: true },
  };

  fadeStickGroup(topSticks, sceneStates[0].top);
  fadeStickGroup(bottomSticks, sceneStates[0].bottom);

  window.updateIndicatorForSceneIndex = function (index) {
    const state = sceneStates[index] || { top: false, bottom: false };
    setIndicatorState(state);
  };
}

(async function init() {
  window.assetLoadingStatus = { progress: 0, isFinished: false };

  initCursor();
  initWordsAnimation();
  startLoader();
  initStickyIndicator();
  initSceneIndicator();
  setupHTMLTexts();
  hideAllOverlays();

  if (DOM.galleryBack) DOM.galleryBack.hidden = true;
  if (DOM.langBtn) DOM.langBtn.hidden = true;
  window.setCarouselActive(false);

  const initParams = { renderer, camera, loadingManager };

  const created = [
    createStart(initParams),
    createCyber(initParams),
    createApps(initParams),
    createContact(initParams),
    createGallery(initParams),
  ];

  State.scenes = await Promise.all(created.map(ensureResolvedScene));
  State.scenes.forEach((s) => {
    if (s && s.onResize) {
      s.onResize(window.innerWidth, window.innerHeight, dpr);
    }
  });
  await Promise.all(State.scenes.map((s) => s.ready || Promise.resolve()));

  updateSizes();

  State.scenes[0]?.activate?.();
  await snapshotSceneToRT(State.scenes[0], rtFrom);

  fsUniforms.tFrom.value = rtFrom.texture;
  fsUniforms.tTo.value = rtFrom.texture;
  fsUniforms.uProgress.value = 1;

  await warmUpTransition();
  applyDeepLink();

  requestAnimationFrame(() => {
    animate();
  });
})();
